import { FilterAltOffOutlined, FilterAltOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Grid, TextField, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { currencyFormatter } from "../../Reading/Bill/Cells/Last12Summary";
import BaseTable from "../../Table/BaseTable";

type GeneratedBillAmounts = {
    powerstationId: number;
    totalAmount: number;
    applicableSurcharge: number;
    payedSurcharge: number;
    surchargeAdj: number;
    collected: number;
    paymentCollectionAmount: number;
    AppliedSurchargeWhenNoCollection: number;
    paymentAmount: number;
    paymentAdjustment: number;
    collectionAdjustment: number;
};

type IssuedBill = {
    powerstationId: number;
    powerstationName: string;
    billsCount: number;
    connectionType: string;
};

type CollectedBill = {
    powerstationId: number;
    powerstationName: string;
    collectionCount: number;
};
type ConsumedUnits = {
    powerstationId: number;
    consumedUnits: number;
};

async function getMonthlySummaryReport(filter: Filter) {
    const controller = "reports/dashboard";

    const [year, month] = filter.month?.split("-") ?? ["", ""];

    try {
        const totalBillsIssuedRes = await GET(
            `/${controller}/bills-generation-count`,
            {
                ...filter,
                connectionType: filter["connection type"] as any,
                "connection type": undefined,
                month: month,
                year: year,
            }
        );

        const totalConsumedunitsRes = await GET(
            `/${controller}/no-of-units-powerstation-wise`,
            {
                ...filter,
                connectionType: filter["connection type"] as any,
                "connection type": undefined,
                month: month,
                year: year,
            }
        );

        const totalGeneratedUnits = await GET("/unit-consumption");

        const totalCollectedBillsRes = await GET(
            `/${controller}/bills-collection-report`,
            {
                ...filter,
                connectionType: filter["connection type"] as any,
                "connection type": undefined,
                month: month,
                year: year,
            }
        );

        const amountOfGeneratedBillsRes = await GET(
            `/${controller}/bill-reading-collections`,
            {
                ...filter,
                connectionType: filter["connection type"] as any,
                "connection type": undefined,
                month: month,
                year: year,
            }
        );

        const data = {
            totalBillsIssued: totalBillsIssuedRes.data.data.filter(
                (d: IssuedBill) => d.powerstationId
            ) as IssuedBill[],
            totalCollectedBills: totalCollectedBillsRes.data
                .data as CollectedBill[],
            amountOfGeneratedBills: amountOfGeneratedBillsRes.data
                .report as GeneratedBillAmounts[],
            consumedUnits: totalConsumedunitsRes.data.noOfBills as any[], // rename if backend key differs
        };

        const getConsumedUnits = (powerstationId: number | string) => {
            return data.consumedUnits
                .filter(
                    (u) =>
                        u.powerstationId?.toString() ===
                        powerstationId?.toString()
                )
                .reduce((sum, u) => sum + (u.consumedUnits ?? 0), 0);
        };

        const getBillAmounts = (powerstationId: string | number) => {
            return (
                data.amountOfGeneratedBills.find(
                    (bill) =>
                        bill.powerstationId?.toString() ===
                        powerstationId?.toString()
                ) ?? null
            );
        };

        const cleanData = data.totalBillsIssued.reduce(
            (
                prev: {
                    [powerstationId: number | string]: {
                        powerstationId: number | string;
                        powerstationName: string;
                        connectionType: string;
                        totalBillsIssued: number;
                        totalBillsCollected: number;
                        totalBillsIssuedAmount: number;
                        totalBillsCollectedAmount: number;
                        dueAmount: number;
                        consumedUnits: number;
                        totalAmountAdjusted: number;
                    };
                },
                curr
            ) => {
                const billAmounts = getBillAmounts(curr.powerstationId);

                const totalBillsIssuedAmount = billAmounts
                    ? billAmounts.totalAmount +
                      billAmounts.applicableSurcharge +
                      billAmounts.AppliedSurchargeWhenNoCollection +
                      billAmounts.paymentAmount
                    : 0;

                const totalBillsCollectedAmount = billAmounts
                    ? billAmounts.collected +
                      billAmounts.payedSurcharge +
                      billAmounts.paymentCollectionAmount
                    : 0;

                const totalAmountAdjusted = billAmounts
                    ? billAmounts?.surchargeAdj +
                      billAmounts.paymentAdjustment +
                      billAmounts.collectionAdjustment
                    : 0;

                const totalBillsCollected =
                    data.totalCollectedBills.find(
                        (bill) => bill.powerstationId === curr.powerstationId
                    )?.collectionCount ?? 0;

                prev[curr.powerstationId] = {
                    powerstationId: curr.powerstationId,
                    powerstationName: curr.powerstationName,
                    connectionType: curr.connectionType,
                    totalBillsIssued: curr.billsCount,
                    totalBillsCollected,
                    totalBillsIssuedAmount,
                    totalBillsCollectedAmount,
                    totalAmountAdjusted,
                    dueAmount:
                        totalBillsIssuedAmount -
                        (totalBillsCollectedAmount + totalAmountAdjusted),
                    consumedUnits: getConsumedUnits(curr.powerstationId),
                };

                return prev;
            },
            {}
        );

        console.log("Clean Data: ", Object.values(cleanData));

        return Object.values(cleanData);
    } catch (error) {
        console.log(error);

        return [];
    }
}

const initialPagination = {
    page: 1,
    limit: 100,
};

type Filter = {
    district: string;
    powerstation: string[];
    startDate: string;
    endDate: string;
    "connection type": string[];
    month: string;
    year: string;
};

const initialFilter: Filter = {
    district: "",
    powerstation: [],
    startDate: "",
    endDate: "",
    "connection type": [],
    month: "",
    year: "",
};

export const MonthlySummaryReport = () => {
    const [refetchCount, setRefetchCount] = useState(0);
    const theme = useTheme();
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const [pagination, setPagination] = useState(initialPagination);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [intermediateFilter, setIntermediateFilter] = useState(initialFilter);
    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        powerstation: false,
        search: false,
        connectionType: false,
        month: false,
        year: false,
    });

    const { data, isLoading } = useQuery(
        ["monthly-summary-report", refetchCount],
        () => getMonthlySummaryReport(filter),
        {
            enabled:
                filter.district !== "" &&
                filter.district !== "all" &&
                filter.powerstation &&
                filter.powerstation.length !== 0,
        }
    );

    function getSummaryRow(cols: string[]) {
        const summaryRow = ["Total"];

        if (!data || data?.length === 0 || !Array.isArray(data)) {
            return summaryRow;
        }

        let totalBillsIssuedSum = 0;
        let totalBillsCollectedSum = 0;
        let billsGeneratedAmountSum = 0;
        let totalBillsCollectionAmountSum = 0;
        let dueAmountSum = 0;
        let consumedUnitsSum = 0;

        for (const row of data) {
            totalBillsIssuedSum += row.totalBillsIssued;
            totalBillsCollectedSum += row.totalBillsCollected;
            billsGeneratedAmountSum += row.totalBillsIssuedAmount;
            totalBillsCollectionAmountSum += row.totalBillsCollectedAmount;
            dueAmountSum += row.dueAmount;
            consumedUnitsSum += row.consumedUnits;
        }

        for (const col of cols) {
            switch (col) {
                case "totalBillsIssued":
                    summaryRow.push(totalBillsIssuedSum as any);
                    break;

                case "totalBillsCollected":
                    summaryRow.push(totalBillsCollectedSum as any);
                    break;

                case "totalBillsIssuedAmount":
                    summaryRow.push(
                        currencyFormatter.format(billsGeneratedAmountSum)
                    );

                    break;

                case "totalBillsCollectedAmount":
                    summaryRow.push(
                        currencyFormatter.format(totalBillsCollectionAmountSum)
                    );

                    break;

                case "dueAmount":
                    summaryRow.push(currencyFormatter.format(dueAmountSum));

                    break;

                case "consumedUnits":
                    summaryRow.push(consumedUnitsSum as any);
                    break;

                default:
                    summaryRow.push("");
            }
        }

        return summaryRow;
    }

    function applyFilter() {
        setFilter(intermediateFilter);
        setRefetchCount((n) => n + 1);
    }

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters

        // reset applied filters
        setFilter(initialFilter);
        setIntermediateFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            search: true,
            connectionType: true,
            month: true,
            year: true,
        });

        // reset search trigger & pagination

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    search: false,
                    connectionType: false,
                    month: false,
                    year: false,
                }),
            0
        );
    };

    return (
        <Box>
            <Grid container spacing={1.5} mt={0.5} position="sticky" top={0}>
                <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                    <Grid item xs={12} md={6} lg={4}>
                        <UpdatedSearchableInput
                            label="District"
                            api="/region/district"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            clearAll={clearAll.district}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                    <Grid item xs={12} md={6} lg={4}>
                        <UpdatedSearchableInput
                            key={intermediateFilter.district}
                            label="Powerstation"
                            api="/powerstation"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            params={{ district: intermediateFilter.district }}
                            multiple={true}
                            clearAll={clearAll.powerstation}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                    <Grid item xs={12} md={6} lg={4}>
                        <UpdatedSearchableInput
                            key={intermediateFilter.district}
                            label="Connection Type"
                            api="/connection"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            params={{
                                district: intermediateFilter.district,
                            }}
                            multiple={true}
                            clearAll={clearAll.connectionType}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                <Grid item xs={12} md={6} lg={4}>
                    <TextField
                        size="small"
                        name="month"
                        label="Month"
                        type="month"
                        fullWidth
                        value={intermediateFilter.month}
                        onChange={(e) =>
                            setIntermediateFilter((f) => ({
                                ...f,
                                month: e.target.value,
                            }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            max: currentMonth, // ✅ prevents forward months
                            min: "2025-10",
                        }}
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <TextField
                        type="date"
                        size="small"
                        value={intermediateFilter.startDate}
                        fullWidth
                        label="From Date"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                            setIntermediateFilter((f) => ({
                                ...f,
                                startDate: e.target.value,
                            }))
                        }
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <TextField
                        type="date"
                        size="small"
                        fullWidth
                        label="To Date"
                        InputLabelProps={{ shrink: true }}
                        value={intermediateFilter.endDate}
                        onChange={(e) =>
                            setIntermediateFilter((f) => ({
                                ...f,
                                endDate: e.target.value,
                            }))
                        }
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={2}
                    // sx={{ minWidth: theme.spacing(20) }}
                >
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={applyFilter}
                        sx={{ height: "100%" }}
                        startIcon={<FilterAltOutlined />}
                        fullWidth
                    >
                        Search
                    </Button>
                </Grid>

                {/* CLEAR ALL */}
                <Grid item xs={12} md={6} lg={2}>
                    <Button
                        color="error"
                        variant="outlined"
                        startIcon={<FilterAltOffOutlined />}
                        onClick={handleClearAll}
                        sx={{
                            height: "100%",
                            minWidth: theme.spacing(20),
                        }}
                        fullWidth
                    >
                        Clear All
                    </Button>
                </Grid>
            </Grid>

            {(filter.district === "all" ||
                !filter.district ||
                !filter.powerstation?.length) && (
                <Box my={2}>
                    <Alert severity="info">
                        Please select district and powerstation to view the
                        report.
                    </Alert>
                </Box>
            )}

            <BaseTable
                summaryRow={getSummaryRow}
                headers={{
                    powerstationName: "Powerstation",
                    totalBillsIssued: "Total Bills Issued",
                    totalBillsCollected: "Total Bills Collected",
                    totalBillsIssuedAmount: "Bills Generated Amount",
                    totalBillsCollectedAmount: "Total Bills Collection Amount",
                    totalAmountAdjusted: "Total Bills Adjusted Amount",
                    consumedUnits: "Consumed Units",
                    dueAmount: "Due Amount",
                    connectionType: "Connection Type",
                }}
                data={(data ?? []).map((item, idx: number) => ({
                    ...item,
                    totalBillsIssuedAmount: currencyFormatter.format(
                        item.totalBillsIssuedAmount
                    ),
                    totalBillsCollectedAmount: currencyFormatter.format(
                        item.totalBillsCollectedAmount
                    ),
                    totalAmountAdjusted: currencyFormatter.format(
                        item.totalAmountAdjusted
                    ),
                    consumedUnits: item.consumedUnits,
                    dueAmount: currencyFormatter.format(item.dueAmount),
                    connectionType: item.connectionType,
                }))}
                delEndPoint={""}
                pagination={pagination}
                setPagination={setPagination}
                rowsCount={0}
                isLoading={
                    // Boolean(filter.district) &&
                    filter.district !== "" &&
                    filter.district !== "all" &&
                    filter.powerstation?.length !== 0 &&
                    isLoading
                }
                permissions={{
                    edit: [""],
                    delete: [""],
                }}
                reportName={"Monthly Summary Report"}
                reportDetails={{
                    district: filter.district ? filter.district : undefined,
                    powerstation: filter.powerstation
                        ? filter.powerstation
                        : undefined,
                    village: filter.village ? filter.village : undefined,
                    hamlet: filter.hamlet ? filter.hamlet : undefined,
                }}
                queryKey={["monthly-summary-report", refetchCount]}
                load={
                    filter.district !== "" &&
                    filter.district !== "all" &&
                    filter.powerstation?.length > 0
                }
                defaultSelectedHeaders={[
                    "powerstationName",
                    "totalBillsIssued",
                    "totalBillsCollected",
                    "totalBillsIssuedAmount",
                    "totalBillsCollectedAmount",
                    "dueAmount",
                    "consumedUnits",
                ]}
                showPagination={false}
            />
        </Box>
    );
};
